import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Button,
  Tooltip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as VehiclesIcon,
  Person as DriversIcon,
  People as CustomersIcon,
  Route as RidesIcon,
  CalendarToday as CalendarIcon,
  EventNote as ShiftPlannerIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  Forum as DriverMessagesIcon,
  Construction as TestModeIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { isCentralRole, logoutUser } from '../../redux/slices/authSlice';
import { subscribeToNotifications, unsubscribeFromNotifications, markAllNotificationsAsRead, isCriticalNotification } from '../../redux/slices/notificationsSlice';
import brandingConfig from '../../config/brandingConfig';
import { getAssetPath } from '../../utils/assetManager';
import alertSoundService from '../../services/alertSoundService';
import tabCoordinator from '../../services/tabCoordinator';
import NotificationCenter from '../notifications/NotificationCenter';
import DriverMessageCenter from '../driverMessages/DriverMessageCenter';
import { useTransportWording } from '../../utils/transportWording';
import { contrastOn } from '../../utils/contrast';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: brandingConfig.colors.error,
    color: 'white',
  },
}));

// Die Leiste traegt die Markenfarbe. Schrift und Hover-Schleier muessen deren
// Helligkeit folgen — fest verdrahtetes Weiss steht auf einem Marken-Gelb
// praktisch unsichtbar da und faellt nur bei dunklen Marken nicht auf.
// Auch im Nachtmodus bleibt es die Original-Markenfarbe (nicht die fuer dunkle
// Flaechen aufgehellte `palette.primary`): Die Logos aller Instanzen sind fuer
// genau diese Flaeche gebaut.
const BRAND_BAR = brandingConfig.colors.primary;
const BRAND_BAR_TEXT = contrastOn(BRAND_BAR);

const NavButton = styled(Button)(({ theme }) => ({
  color: BRAND_BAR_TEXT,
  marginRight: theme.spacing(0.5),
  padding: theme.spacing(1, 1),
  minWidth: 0,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: alpha(BRAND_BAR_TEXT, 0.1),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  marginRight: theme.spacing(2),
  cursor: 'pointer',
  overflow: 'hidden',
  // Sonst drueckt die Navigation das Logo auf 0 px zusammen.
  flexShrink: 0
}));

// White-Label-Anpassung: Dieser Mandant führt nur EIN Logo (Kombi-Marke).
// Der Schriftzug daneben ist entfernt, sonst erschiene die Marke doppelt.
const LogoImg = styled('img')(({ theme }) => ({
  height: 52,
  marginRight: theme.spacing(0)
}));

// Als Funktion, weil der Schichtkalender je nach Unternehmensform
// „Taxi-Kalender" oder „Wagen-Kalender" heisst
// (nextcab_mobile/docs/general/unternehmensform_konzept.md).
const buildPages = (wording) => [
  { name: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { name: 'Fahrer', path: '/fahrer', icon: <DriversIcon /> },
  { name: 'Kunden', path: '/kunden', icon: <CustomersIcon /> },
  { name: 'Fahrzeuge', path: '/fahrzeuge', icon: <VehiclesIcon /> },
  { name: 'Fahrten', path: '/fahrten', icon: <RidesIcon /> },
  { name: `${wording.vehicle}-Kalender`, path: '/schichten/kalender', icon: <CalendarIcon /> },
  { name: 'Schichtplaner', path: '/schichtplaner', icon: <ShiftPlannerIcon /> },
  { name: 'Berichte', path: '/berichte', icon: <ReportsIcon /> },
  { name: 'Einstellungen', path: '/einstellungen', icon: <SettingsIcon /> },
];

// Platz fuer die neun Menuepunkte (gemessen mit NextCab-Logo, Testmodus-Chip
// und den drei Symbolen rechts): Unterhalb von md gibt es das Drawer-Menue,
// darueber je nach Breite nur Symbole, nur Text oder beides.
const NAV_TEXT_QUERY = '(min-width:1340px)';
const NAV_FULL_QUERY = '(min-width:1600px)';

const getUserDisplayName = (user) => (
  user?.displayName || user?.name || user?.email || 'Benutzer'
);

const getUserInitial = (user) => {
  const displayName = getUserDisplayName(user).trim();
  return displayName ? displayName.charAt(0).toUpperCase() : 'U';
};

// Entstehungszeit einer Benachrichtigung in Millisekunden; null, wenn unbekannt.
// Speist den Altersfilter des tabCoordinator, damit ein nach einem Reconnect
// nachgelieferter Stau nicht nachtraeglich vertont wird.
const notificationCreatedAtMs = (notification) => {
  try {
    const value = notification?.createdAt;
    if (!value) return null;
    const date = value.toDate ? value.toDate() : new Date(value);
    const ms = date.getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch (_) {
    return null;
  }
};

const Navbar = () => {
  const dispatch = useDispatch();
  const wording = useTransportWording();
  const pages = buildPages(wording);
  const navigate = useNavigate();
  
  const { user } = useSelector((state) => state.auth);
  const { unreadCount, criticalCount, notifications } = useSelector((state) => state.notifications);

  // Testmodus der Kunden-App: dauerhaft sichtbar, solange er läuft. Ein
  // vergessener Schalter ist ein unsichtbarer Totalausfall des Bestellkanals —
  // deshalb steht der Hinweis in der Navigation und nicht nur in den
  // Einstellungen. `useTransportWording` hat die Einstellungen bereits geladen.
  // nextcab_mobile/docs/general/testmodus_kunden_app_konzept.md
  const testModeActive = useSelector((state) => state.settings?.global?.testModusAktiv) === true;
  const testModeUntil = useSelector((state) => state.settings?.global?.testModusBis) || null;
  const testModeOverdue = testModeActive && testModeUntil
    ? new Date(`${testModeUntil}T23:59:59`) < new Date()
    : false;
  const testModeLabel = testModeUntil && !testModeOverdue
    ? `Testmodus bis ${new Date(testModeUntil).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`
    : 'Testmodus aktiv';
  
  // Remove anchorElNav to fix unused variable warning
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [anchorElDriverMessages, setAnchorElDriverMessages] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navShowsText = useMediaQuery(NAV_TEXT_QUERY);
  const navShowsIcons = useMediaQuery(NAV_FULL_QUERY) || !navShowsText;
  // Der Testmodus-Chip braucht mit Text ca. 140 px; wo es eng ist, bleibt nur
  // das Symbol (Text im Tooltip).
  const testModeCompact = !navShowsText;

  // Echtzeit-Listener für Benachrichtigungen, wenn Benutzer eingeloggt ist.
  // (Überfällige Vorbestellungen meldet der Server: portal_preorder_not_started —
  // das frühere client-seitige Polling via rideMonitoringService entfällt.)
  useEffect(() => {
    if (user) {
      dispatch(subscribeToNotifications({ userId: user.uid }));

      return () => {
        dispatch(unsubscribeFromNotifications());
      };
    } else {
      dispatch(unsubscribeFromNotifications());
    }
  }, [dispatch, user]);

  // Warnton bei neu eintreffenden kritischen Benachrichtigungen (aus dem
  // Firestore-Listener, funktioniert damit für alle serverseitigen Events)
  const seenCriticalIdsRef = useRef(null);

  useEffect(() => {
    // Bei Benutzerwechsel/Logout zurücksetzen, damit der erste Snapshot
    // des nächsten Logins nicht als "neu" alarmiert
    seenCriticalIdsRef.current = null;
  }, [user]);

  useEffect(() => {
    if (!user || !notifications) return;

    const critical = notifications.filter((n) => !n.read && isCriticalNotification(n));

    if (seenCriticalIdsRef.current === null) {
      // Erste Snapshot-Lieferung nach Login: nur merken, nicht alarmieren
      seenCriticalIdsRef.current = new Set(critical.map((n) => n.id));
      return;
    }

    const fresh = critical.filter((n) => !seenCriticalIdsRef.current.has(n.id));
    critical.forEach((n) => seenCriticalIdsRef.current.add(n.id));

    if (fresh.length > 0) {
      // Der Ton laeuft ueber den tabCoordinator: Bei mehreren offenen Fenstern
      // spielt nur eines, und ein Ereignisstau nach Reconnect wird nicht
      // nachtraeglich vertont (siehe docs/KONZEPT_MEHRFACHE_PORTAL_INSTANZEN.md).
      // Massgeblich ist die juengste neue Meldung, damit ein alter Nachzuegler
      // im selben Snapshot den Altersfilter nicht ausloest.
      const newestMs = fresh.reduce((max, n) => {
        const ms = notificationCreatedAtMs(n);
        return ms !== null && ms > max ? ms : max;
      }, 0);
      tabCoordinator.playSound(
        `critical:${fresh.map((n) => n.id).sort().join(',')}`,
        () => alertSoundService.playAlertSound(),
        newestMs > 0 ? { occurredAtMs: newestMs } : {},
      );
    }
  }, [user, notifications]);
  
  // Diese useEffect-Hook ist nicht mehr nötig, da wir die Adminsitzung beibehalten
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  
  const handleOpenNotificationsMenu = (event) => {
    setAnchorElNotifications(event.currentTarget);
    
    // NICHT automatisch als gelesen markieren beim Öffnen
    // Das verursacht einen State-Update der das Menu sofort schließt
    // Stattdessen nur beim Schließen markieren
  };

  const handleOpenDriverMessagesMenu = (event) => {
    setAnchorElDriverMessages(event.currentTarget);
  };

  // Removed handleCloseNavMenu to fix unused variable warning

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleCloseNotificationsMenu = () => {
    // Markiere alle Benachrichtigungen als gelesen beim Schließen
    if (user && unreadCount > 0) {
      dispatch(markAllNotificationsAsRead(user.uid));
    }
    
    setAnchorElNotifications(null);
  };

  const handleCloseDriverMessagesMenu = () => {
    setAnchorElDriverMessages(null);
  };
  
  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };
  
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
    handleCloseUserMenu();
  };

  const DrawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', overflow: 'hidden', maxHeight: '100px' }}>
        <img 
          src={getAssetPath('icon', true)} 
          alt={`${brandingConfig.appName} Logo`} 
          style={{ height: 52, marginRight: 0 }} 
        />
      </Box>
      <Divider />
      <List>
        {pages.map((page) => (
          <ListItem button key={page.name} component={Link} to={page.path}>
            <ListItemIcon>
              {page.icon}
            </ListItemIcon>
            <ListItemText primary={page.name} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" enableColorOnDark sx={{ bgcolor: BRAND_BAR, color: BRAND_BAR_TEXT }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ height: '64px', overflow: 'hidden' }}>
            {/* Desktop Logo */}
            <LogoContainer 
              component={Link}
              to="/"
              sx={{ display: { xs: 'none', md: 'flex' }, maxHeight: '64px' }}
            >
              <LogoImg 
                src={getAssetPath('icon', true)} 
                alt={`${brandingConfig.appName} Logo`} 
              />
            </LogoContainer>

            {/* Mobile Menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="Menü öffnen"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={toggleDrawer(true)}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            
            {/* Mobile Logo */}
            <LogoContainer 
              component={Link}
              to="/"
              sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, flexShrink: 1, minWidth: 0, justifyContent: 'flex-start', ml: 1 }}
            >
              <LogoImg 
                src={getAssetPath('icon', true)} 
                alt={`${brandingConfig.appName} Logo`} 
                sx={{ height: 52 }} 
              />
            </LogoContainer>
            
            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {pages.map((page) => (navShowsText ? (
                <NavButton
                  key={page.name}
                  component={Link}
                  to={page.path}
                  startIcon={navShowsIcons ? page.icon : undefined}
                >
                  {page.name}
                </NavButton>
              ) : (
                <Tooltip key={page.name} title={page.name}>
                  <NavButton component={Link} to={page.path} aria-label={page.name}>
                    {page.icon}
                  </NavButton>
                </Tooltip>
              )))}
            </Box>

            {/* User and Notifications */}
            <Box sx={{ flexGrow: 0, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {user && (
                <>
                  {testModeActive && (
                    <Tooltip
                      title={testModeOverdue
                        ? 'Der Testmodus läuft über das angekündigte Ende hinaus. Fahrgäste können in der App weiterhin nicht bestellen.'
                        : 'Fahrgäste können in der App derzeit nicht bestellen. Zum Beenden in die Einstellungen.'}
                    >
                      <Chip
                        icon={<TestModeIcon />}
                        label={testModeOverdue ? 'Testmodus überfällig' : testModeLabel}
                        aria-label={testModeOverdue ? 'Testmodus überfällig' : testModeLabel}
                        color={testModeOverdue ? 'error' : 'warning'}
                        size="small"
                        onClick={() => navigate('/einstellungen')}
                        sx={{
                          mr: 1,
                          fontWeight: 'bold',
                          ...(testModeCompact && {
                            '& .MuiChip-label': { display: 'none' },
                            '& .MuiChip-icon': { mx: 0.5 },
                          }),
                        }}
                      />
                    </Tooltip>
                  )}

                  <Tooltip title={criticalCount > 0 ? `⚠️ ${criticalCount} kritische Meldung(en)` : "Benachrichtigungen öffnen"}>
                    <IconButton
                      onClick={handleOpenNotificationsMenu}
                      sx={{
                        p: 1,
                        mr: 1,
                        ...(criticalCount > 0 && {
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': {
                              transform: 'scale(1)',
                            },
                            '50%': {
                              transform: 'scale(1.05)',
                            },
                            '100%': {
                              transform: 'scale(1)',
                            },
                          },
                        })
                      }}
                      color="inherit"
                    >
                      <StyledBadge
                        badgeContent={unreadCount}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            backgroundColor: criticalCount > 0 ? '#ff4444' : brandingConfig.colors.error,
                            animation: criticalCount > 0 ? 'blink 1.5s infinite' : 'none',
                            '@keyframes blink': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.3 },
                              '100%': { opacity: 1 },
                            },
                          }
                        }}
                      >
                        <NotificationsIcon sx={{
                          color: criticalCount > 0 ? '#ff4444' : 'inherit'
                        }} />
                      </StyledBadge>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Fahrer-Nachrichten">
                    <IconButton
                      onClick={handleOpenDriverMessagesMenu}
                      sx={{ p: 1, mr: 1 }}
                      color="inherit"
                      aria-label="Fahrer-Nachrichten öffnen"
                    >
                      <DriverMessagesIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Einstellungen öffnen">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar 
                        alt={getUserDisplayName(user)} 
                        src={user.photoURL || ''} 
                        sx={{ bgcolor: 'grey.900', color: 'common.white' }}
                      >
                        {getUserInitial(user)}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              {/* Notifications Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-notifications"
                anchorEl={anchorElNotifications}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElNotifications)}
                onClose={handleCloseNotificationsMenu}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: { xs: 'calc(100vw - 24px)', sm: 400 },
                    maxWidth: { xs: 'calc(100vw - 24px)', sm: 500 },
                    maxHeight: 600,
                    overflow: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <NotificationCenter
                  onClose={handleCloseNotificationsMenu}
                />
              </Menu>

              {/* Driver Messages Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-driver-messages"
                anchorEl={anchorElDriverMessages}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElDriverMessages)}
                onClose={handleCloseDriverMessagesMenu}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: { xs: 'calc(100vw - 24px)', sm: 420 },
                    maxWidth: { xs: 'calc(100vw - 24px)', sm: 520 },
                    maxHeight: 680,
                    overflow: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <DriverMessageCenter
                  user={user}
                />
              </Menu>
              
              {/* User Menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem component={Link} to="/profil" onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">Profil</Typography>
                </MenuItem>
                {isCentralRole(user) && (
                  <MenuItem component={Link} to="/profil/unternehmer" onClick={handleCloseUserMenu}>
                    <ManageAccountsIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography textAlign="center">Unternehmer & Sub-Konten</Typography>
                  </MenuItem>
                )}
                <MenuItem component={Link} to="/einstellungen" onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">Einstellungen</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center">Abmelden</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <DrawerList />
      </Drawer>
    </>
  );
};

export default Navbar;
